/**
 * src/hooks/useCopy.ts
 *
 * Fetches a plain-text guidance file from public/content/ at runtime and returns
 * the cleaned string. This keeps interface copy out of component files — swap or
 * revise any .txt file without touching application logic.
 *
 * Bracketed section header lines (e.g. "[MODAL INTRO / VOICE ON LOAD]") are
 * stripped before the text is returned. Paragraph breaks (\n\n) are preserved
 * so callers can render them as <p> elements.
 *
 * Usage:
 *   const text = useCopy('modal-intro.txt');
 *   const text = useCopy('main-screen-guidance.txt', 'Fallback if fetch fails.');
 *
 * Files live at: public/content/<filename>
 * Served at runtime from: /content/<filename>
 *
 * TTS note: the modal-intro.txt content doubles as the opening voice narration
 * script. When ElevenLabs TTS is wired up, dispatch userSpeak(text) with this
 * string unless a separate voice script file is added.
 */

import { useState, useEffect } from 'react';

function stripBracketedHeaders(raw: string): string {
  return raw
    .split('\n')
    .filter(line => !/^\s*\[.*\]\s*$/.test(line))
    .join('\n')
    .trim();
}

export function useCopy(filename: string, fallback = ''): string {
  const [text, setText] = useState('');

  useEffect(() => {
    const url = `${process.env.PUBLIC_URL || ''}/content/${filename}`;
    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`useCopy: ${r.status} fetching ${filename}`);
        return r.text();
      })
      .then(raw => setText(stripBracketedHeaders(raw)))
      .catch(err => {
        console.warn(err.message);
        setText(fallback);
      });
  }, [filename, fallback]);

  return text;
}
